/**
 * App store (04 Shell implementation notes): one tiny observable store that drives the controls
 * and the URL hash deep link `#race=2024-monza&t=3227.1&mode=director&follow=LEC&view=cx,cy,z`.
 *
 * - `view=cx,cy,z`: world centre (m) and the zoom **relative to the fitted preset**
 *   (z = presetMpp / mpp, so 2 = twice as close), so a shared view frames the same area on a
 *   different display. Written only when the camera is away from the preset.
 * - `mode`: `director` or a free rate `1` / `5` / `20` / `60`.
 * - `play=1` autoplays after load (default: paused at the restored time).
 * - `take=<n>`: the commentary take, 1-based (C10); written by the START pick so the take is
 *   replayable. Absent = take 1.
 * - `lite=auto|1|0` (r14): the LITE setting; written only when it isn't `auto`.
 * - The hash is written with `history.replaceState` (no history spam), throttled.
 */
import type { AppState, Mode } from '../core/api.ts';
import { parseLiteSetting, type LiteSetting } from '../core/layout/lite.ts';

export type Listener<T> = (state: Readonly<T>, prev: Readonly<T>) => void;

/** Minimal observable store: `set` merges a patch and notifies when anything changed. */
export class Store<T extends object> {
  private state: T;
  private readonly listeners: Listener<T>[] = [];

  constructor(initial: T) {
    this.state = { ...initial };
  }

  get(): Readonly<T> {
    return this.state;
  }

  set(patch: Partial<T>): void {
    const prev = this.state;
    let changed = false;
    for (const k of Object.keys(patch) as (keyof T)[]) {
      if (!Object.is(prev[k], patch[k]) && !(isObj(prev[k]) && isObj(patch[k]) && JSON.stringify(prev[k]) === JSON.stringify(patch[k]))) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.state = { ...prev, ...patch };
    for (const fn of this.listeners.slice()) fn(this.state, prev);
  }

  subscribe(fn: Listener<T>): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }
}

function isObj(v: unknown): boolean {
  return typeof v === 'object' && v !== null;
}

/** Deep-link view: world centre and zoom relative to the fitted preset (z = presetMpp / mpp). */
export interface DeepView {
  cx: number;
  cy: number;
  z: number;
}

export interface DeepLink {
  race?: string | undefined;
  t?: number;
  mode?: Mode;
  follow?: string;
  view?: DeepView;
  play?: boolean;
  /** the commentary take, 1-based (C10): `take=7` → take 6. Only integers >= 1 parse; whether the
   *  race has that many takes is the player's to check (the file is not known here). */
  take?: number;
  /** the LITE setting (r14): `lite=1` on, `lite=0` off; absent = the remembered choice, else auto */
  lite?: LiteSetting;
}

const RATES = [1, 5, 20, 60] as const;
type Rate = (typeof RATES)[number];

export function isRate(n: number): n is Rate {
  return (RATES as readonly number[]).includes(n);
}

export function modeToString(m: Mode): string {
  return m.kind === 'director' ? 'director' : String(m.rate);
}

export function parseMode(s: string): Mode | undefined {
  const v = s.trim().toLowerCase();
  if (v === 'director' || v === 'd') return { kind: 'director' };
  const n = Number(v.replace(/x$/, ''));
  return isRate(n) ? { kind: 'free', rate: n } : undefined;
}

/** Parse a location hash ('#a=b&c=d' or 'a=b&c=d'). Invalid fields are ignored. */
export function parseHash(hash: string): DeepLink {
  const out: DeepLink = {};
  const q = new URLSearchParams(hash.replace(/^#/, ''));
  const race = q.get('race');
  if (race && race.trim() !== '') out.race = race.trim();
  const t = q.get('t');
  if (t !== null && t.trim() !== '') {
    const n = Number(t);
    if (Number.isFinite(n) && n >= 0) out.t = n;
  }
  const mode = q.get('mode');
  if (mode) {
    const m = parseMode(mode);
    if (m) out.mode = m;
  }
  const follow = q.get('follow');
  if (follow && /^[A-Za-z]{3}$/.test(follow)) out.follow = follow.toUpperCase();
  const view = q.get('view');
  if (view) {
    const p = view.split(',').map(Number);
    const [cx, cy, z] = p;
    if (p.length === 3 && cx !== undefined && cy !== undefined && z !== undefined && p.every(Number.isFinite) && z > 0) {
      out.view = { cx, cy, z };
    }
  }
  const play = q.get('play');
  if (play !== null) out.play = play === '1' || play === 'true';
  const take = q.get('take');
  if (take !== null && take.trim() !== '') {
    const n = Number(take);
    if (Number.isInteger(n) && n >= 1) out.take = n;
  }
  const lite = parseLiteSetting(q.get('lite'));
  if (lite) out.lite = lite;
  return out;
}

/** Shortest decimal for a number at a fixed precision (no trailing zeros). */
function num(v: number, digits: number): string {
  const s = v.toFixed(digits);
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/** Format the deep link (field order: race, t, mode, follow, view, play, take, lite). */
export function formatHash(l: DeepLink): string {
  const parts: string[] = [];
  if (l.race) parts.push(`race=${l.race}`);
  if (l.t !== undefined) parts.push(`t=${num(l.t, 1)}`);
  if (l.mode) parts.push(`mode=${modeToString(l.mode)}`);
  if (l.follow) parts.push(`follow=${l.follow}`);
  if (l.view) parts.push(`view=${num(l.view.cx, 1)},${num(l.view.cy, 1)},${num(l.view.z, 3)}`);
  if (l.play) parts.push('play=1');
  if (l.take !== undefined) parts.push(`take=${String(l.take)}`);
  if (l.lite && l.lite !== 'auto') parts.push(`lite=${l.lite === 'on' ? '1' : '0'}`);
  return parts.length ? `#${parts.join('&')}` : '';
}

export const DEFAULT_STATE: AppState = {
  raceTime: 0,
  playing: false,
  mode: { kind: 'director' },
  follow: null,
};

/**
 * Writes the deep link to `location.hash` (replaceState), at most every `intervalMs` while the
 * playhead moves, immediately for discrete changes (mode, follow).
 */
export class HashWriter {
  private last = '';
  private lastWrite = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly build: () => DeepLink,
    private readonly intervalMs = 500,
  ) {}

  /** Request a write; `now` skips the throttle. */
  request(now = false): void {
    const t = performance.now();
    if (now || t - this.lastWrite >= this.intervalMs) {
      this.write();
      return;
    }
    if (!this.timer) {
      this.timer = setTimeout(() => {
        this.timer = null;
        this.write();
      }, this.intervalMs - (t - this.lastWrite));
    }
  }

  write(): void {
    this.lastWrite = performance.now();
    const h = formatHash(this.build());
    if (h === this.last) return;
    this.last = h;
    const url = `${location.pathname}${location.search}${h}`;
    history.replaceState(history.state, '', url);
  }

  dispose(): void {
    if (this.timer) clearTimeout(this.timer);
  }
}
